import IntercomLauncherDependantElement from "@entrilia/shared/components/IntercomLauncherDependantElement.tsx";
import { Grid } from "@mui/material";
import type { GridPinnedColumnFields, GridRowGroupingModel, GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useRef } from "react";
import type {
  CalculationType,
  ColumnHorizontalAlignment,
} from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  GroupingField,
  PreviewMode,
  ReportField,
  SortField,
  TabularDataCell,
  TabularGeneralSettings,
} from "../../../../../../../api/types/svTypes.ts";
import useResizeObserver from "../../../hooks/resizeObserver.ts";
import { PreviewDefaultSize } from "../../../utilities/const.ts";
import { isAggregationBased, isCustomColumn } from "../../../utilities/fieldsHelper.ts";
import { stopEventPropagation } from "../../../utilities/stopEventPropagation.ts";
import type { DataGridCustomProps } from "../../common/DataGridCustomProps.tsx";
import ReportRefreshError from "../../common/ReportRefreshError.tsx";
import BiErrorCodeComponent from "../common/bi-error/BiErrorCodeComponent.tsx";
import { hasAvailableDrillDown } from "../common/hasAvailableDrillDown.ts";
import PreviewComponentAlert from "../common/PreviewComponentAlert.tsx";
import ValidationState from "../common/ValidationState.tsx";
import type { TabularGridState } from "./hooks/gridState.types.ts";
import TabularPreviewGrid from "./TabularPreviewGrid.tsx";

interface Props extends Pick<DataGridCustomProps, "disableColumnManagement" | "disableCellClick"> {
  gridState: TabularGridState;
  onRefresh?: () => void;
  fields: ReportField[];
  groups: GroupingField[];
  previewMode: PreviewMode;
  settings: TabularGeneralSettings;
  sorts: SortField[];
  onGroupingChanged?: (model: GridRowGroupingModel) => void;
  onColumnsSortingChanged?: (gridSortModel: GridSortModel) => void;
  onColumnsOrderChanged?: (names: string[]) => void;
  onDrillDown?: (field: ReportField, cell: TabularDataCell) => void;
  onFreezedColumnsChange?: (freezedColumns: GridPinnedColumnFields) => void;
  onColumnWidthChanged?: (field: string, width: number | undefined, applyToAll?: boolean) => void;
  onColumnAlignmentChanged?: (field: string, alignment: ColumnHorizontalAlignment | undefined) => void;
  onPredefinedCustomFieldType:
    ((calculationType: CalculationType, fieldGuid: string, anchorEl: HTMLElement | null) => void) | undefined;
}

export default function PreviewComponent({
  gridState,
  onRefresh,
  fields,
  groups,
  onGroupingChanged,
  onColumnsSortingChanged,
  onColumnsOrderChanged,
  onDrillDown,
  onFreezedColumnsChange,
  onColumnWidthChanged,
  onColumnAlignmentChanged,
  previewMode,
  settings,
  sorts,
  disableColumnManagement,
  disableCellClick,
  onPredefinedCustomFieldType,
}: Props) {
  const divRef = useRef<HTMLDivElement>(null);
  const size = useResizeObserver(divRef, undefined, previewMode === "maximize" ? undefined : PreviewDefaultSize);
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const handleDrillDown = useCallback(
    (cell: TabularDataCell | undefined, fieldGuid: string) => {
      if (onDrillDown === undefined || !cell || !hasAvailableDrillDown(cell.drillDownId)) {
        return;
      }

      const field = fieldsRef.current.find((f) => f.config.guid === fieldGuid);
      if (!isAggregationBased(field) && !isCustomColumn(field)) {
        return;
      }

      onDrillDown(field, cell);
    },
    [onDrillDown]
  );

  const fieldsAreEmpty = fields.length === 0;

  if (fieldsAreEmpty) return <></>;

  return (
    <BiErrorCodeComponent errorMessage={gridState.error}>
      {gridState.error ? (
        <ReportRefreshError error={gridState.error} hasPreviousResult={gridState.hasResult} />
      ) : (
        <PreviewComponentAlert error={undefined} failedMeasures={gridState.failedMeasures} />
      )}
      <ValidationState state={gridState.configurationValidity} fields={getAggregationBased(fields)}>
        <IntercomLauncherDependantElement mode="opacity">
          <Grid
            ref={divRef}
            container
            sx={{ flex: 1, justifyContent: "center", width: "100%" }}
            onDrop={stopEventPropagation}
          >
            <TabularPreviewGrid
              onRefresh={onRefresh}
              snapshot={gridState.snapshot}
              rows={gridState.rows}
              columns={gridState.columns}
              grandTotals={gridState.grandTotals}
              groupTotals={gridState.groupTotals}
              groupingFields={groups}
              loading={gridState.loading}
              size={size}
              onGroupingChanged={onGroupingChanged}
              onColumnsSortingChanged={onColumnsSortingChanged}
              onColumnsOrderChanged={onColumnsOrderChanged}
              onDrillDown={handleDrillDown}
              onFreezedColumnsChange={onFreezedColumnsChange}
              onColumnWidthChanged={onColumnWidthChanged}
              onColumnAlignmentChanged={onColumnAlignmentChanged}
              settings={settings}
              fields={fields}
              sorts={sorts}
              disableColumnManagement={!!disableColumnManagement}
              disableCellClick={!!disableCellClick}
              onPredefinedCustomFieldType={onPredefinedCustomFieldType}
            />
          </Grid>
        </IntercomLauncherDependantElement>
      </ValidationState>
    </BiErrorCodeComponent>
  );
}

function getAggregationBased(fields: ReportField[]): ReportField[] {
  return fields.filter(isAggregationBased);
}
